import React, { useEffect, useState } from 'react';
import { useAuthFetch } from './AuthComponent';

/**
 * Report Issue modal — post-shipment (and pre-shipment) escalation flow.
 *
 * Modeled after QCFailModal. Reuses the same qcp-modal-* CSS classes so the
 * visual is identical to the QC Fail modal. Fetches reasons from
 * /api/packing/settings (qa_fail_reasons, filtered to !archived).
 *
 * Props:
 *   orderName   — order # for the header (e.g. "SP-259337")
 *   onSubmit    — async (reason, note) => void. Modal closes on resolve.
 *   onCancel    — () => void
 */
export default function ReportIssueModal({ orderName, onSubmit, onCancel }) {
    const fetchAuth = useAuthFetch();
    const [reasons, setReasons] = useState([]);
    const [reasonsLoading, setReasonsLoading] = useState(true);
    const [reason, setReason] = useState('');
    const [savedNote, setSavedNote] = useState('');
    const [noteEditing, setNoteEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setReasonsLoading(true);
        fetchAuth('/api/packing/settings')
            .then(r => r.json())
            .then(d => setReasons(
                (d.qa_fail_reasons || [])
                    .filter(r => !(typeof r === 'object' && r.archived))
                    .map(r => (typeof r === 'object' ? r.text : r))
                    .filter(Boolean)
            ))
            .catch(() => setReasons([]))
            .finally(() => setReasonsLoading(false));
    }, [fetchAuth]);

    function handleSelectReason(r) {
        if (r === reason) return;
        setReason(r);
        setSavedNote('');
        setDraft('');
        setNoteEditing(false);
    }

    function openNoteEditor(e) {
        e.preventDefault();
        e.stopPropagation();
        setDraft(savedNote);
        setNoteEditing(true);
    }

    function saveNote() {
        setSavedNote(draft.trim().slice(0, 500));
        setNoteEditing(false);
    }

    function cancelNote() {
        setNoteEditing(false);
        setDraft('');
    }

    function notePreview(text) {
        if (!text) return '';
        return text.length > 80 ? text.slice(0, 80) + '…' : text;
    }

    async function handleSubmit() {
        const effective = (noteEditing ? draft : savedNote).trim().slice(0, 500) || null;
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit(reason, effective);
        } catch (e) {
            setError(String(e.message || e));
            setSubmitting(false);
        }
    }

    return (
        <div className="qcp-modal-overlay">
            <div className="qcp-modal">
                <div className="qcp-modal-title">Report Issue{orderName ? ` — ${orderName}` : ''}</div>
                <div className="qcp-modal-sub">Select an issue reason</div>
                <div className="qcp-modal-reasons">
                    {reasonsLoading && (
                        <div className="qcp-modal-reason">Loading reasons…</div>
                    )}
                    {!reasonsLoading && reasons.length === 0 && (
                        <div className="qcp-modal-reason">
                            No reasons configured. Add one in Settings → QC Fail Reasons.
                        </div>
                    )}
                    {reasons.map(r => {
                        const selected = reason === r;
                        const showEditor = selected && noteEditing;
                        const showPreview = selected && !noteEditing && savedNote;
                        return (
                            <div
                                key={r}
                                className={`qcp-modal-reason${selected ? ' qcp-modal-reason--selected' : ''}`}
                            >
                                <label className="qcp-modal-reason__row">
                                    <input
                                        type="radio" name="issue-reason" value={r}
                                        checked={selected}
                                        onChange={() => handleSelectReason(r)}
                                    />
                                    <span className="qcp-modal-reason__label">{r}</span>
                                    {selected && !noteEditing && (
                                        <button
                                            type="button"
                                            className="qcp-modal-reason__note-pill"
                                            onClick={openNoteEditor}
                                        >
                                            {savedNote ? 'Edit Note' : 'Add Note'}
                                        </button>
                                    )}
                                </label>
                                {showPreview && (
                                    <div className="qcp-modal-reason__note-preview">
                                        {notePreview(savedNote)}
                                    </div>
                                )}
                                {showEditor && (
                                    <div className="qcp-modal-reason__note-edit">
                                        <textarea
                                            className="qcp-modal-reason__note-textarea"
                                            value={draft}
                                            onChange={(e) => setDraft(e.target.value)}
                                            placeholder="Type a note for this issue (optional)"
                                            rows={3}
                                            maxLength={500}
                                            autoFocus
                                            inputMode="text"
                                        />
                                        <div className="qcp-modal-reason__note-actions">
                                            <button
                                                type="button"
                                                className="qcp-modal-btn qcp-modal-btn--secondary"
                                                onClick={cancelNote}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="qcp-modal-btn qcp-modal-btn--primary"
                                                onClick={saveNote}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {error && (
                    <div style={{ color: '#c92a2a', padding: '8px 4px', fontSize: 13 }}>
                        {error}
                    </div>
                )}
                <div className="qcp-modal-actions">
                    <button
                        className="qcp-modal-btn qcp-modal-btn--secondary"
                        onClick={onCancel}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        className="qcp-modal-btn qcp-modal-btn--danger"
                        onClick={handleSubmit}
                        disabled={!reason || submitting}
                    >
                        {submitting ? 'Reporting…' : 'Report Issue'}
                    </button>
                </div>
            </div>
        </div>
    );
}
